import { useEffect, useRef } from 'react';
import { FrameWatch } from '../frameWatch';
import {
  flaring,
  getScene,
  rgb,
  SMOKE,
  subscribeScene,
  type Palette,
  type Rgb,
  type Scene,
} from '../scene';
import { compile, softwareRendered } from '../webgl';
import { StarField } from './StarField';

/*
 * The void behind the game: slow smoke of violet and deep blue turning around the middle of the
 * screen, over black, and stars in front of it (StarField, drawn at the screen's own resolution:
 * this canvas has too few pixels for points of light). An original WebGL shader written for
 * PAWNED (see CREDITS.md): domain-warped fractal noise, twisted into a gentle vortex. It renders
 * at a fraction of the screen resolution (smoke is soft anyway), at most 30 frames a second, with
 * fewer pixels on a device that cannot keep up (src/ui/frameWatch.ts), not at all while the page
 * is hidden, and as one still frame when motion is reduced, when the graphics are drawn by the
 * processor, or when even the fewest pixels are too many. Without WebGL the page keeps its CSS
 * gradient.
 *
 * In the Long Night the smoke takes the scene's palette, speed and twist (src/ui/scene.ts,
 * docs/LONG_NIGHT_ART_DIRECTION.md §6.4): each Night darker and colder, each regular's table its
 * own, the Back Room oxblood. A change cross-fades over 2.4 s, so the smoke never cuts.
 */

const VERTEX = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAGMENT = `
precision mediump float;
uniform vec2 u_res;
uniform float u_time;
uniform vec3 u_void;
uniform vec3 u_deep;
uniform vec3 u_body;
uniform vec3 u_current;
uniform vec3 u_wisp;
uniform float u_twist;
uniform float u_desat;
// His eyes at Closing Time, in canvas pixels: the smoke turns round each and hollows under it.
uniform vec2 u_eyeA;
uniform vec2 u_eyeB;
uniform float u_eyeR;
uniform float u_eyes;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  mat2 r = mat2(0.8, -0.6, 0.6, 0.8);
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = r * p * 2.03 + 0.17;
    a *= 0.5;
  }
  return v;
}

mat2 turn(float a) {
  float c = cos(a);
  float s = sin(a);
  return mat2(c, -s, s, c);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  vec2 p = (gl_FragCoord.xy - 0.5 * u_res) / min(u_res.x, u_res.y);
  float t = u_time;
  // A slow vortex: the middle turns more than the edges, and the whole drifts round.
  float r = length(p);
  p = turn(t * 0.018 + 2.1 * u_twist * exp(-r * 1.25) * (0.85 + 0.15 * sin(t * 0.07))) * p;
  // Round his eyes the smoke is drawn into two slow whirlpools.
  float unit = min(u_res.x, u_res.y);
  vec2 ea = (u_eyeA - 0.5 * u_res) / unit;
  vec2 eb = (u_eyeB - 0.5 * u_res) / unit;
  float er = u_eyeR / unit;
  vec2 p0 = (gl_FragCoord.xy - 0.5 * u_res) / unit;
  if (u_eyes > 0.0) {
    float ka = exp(-length(p - ea) / (er * 2.2));
    p = ea + turn(u_eyes * (3.2 * ka + t * 0.06 * ka)) * (p - ea);
    float kb = exp(-length(p - eb) / (er * 2.2));
    p = eb + turn(-u_eyes * (3.2 * kb + t * 0.06 * kb)) * (p - eb);
  }
  vec2 s = p * 2.0;
  vec2 q = vec2(fbm(s + vec2(0.0, t * 0.035)), fbm(s + vec2(5.2, 1.3) - t * 0.03));
  vec2 w = vec2(
    fbm(s + 3.3 * q + vec2(1.7, 9.2) + t * 0.022),
    fbm(s + 3.3 * q + vec2(8.3, 2.8) - t * 0.02)
  );
  float d = fbm(s + 3.6 * w);

  vec3 voidc = u_void;
  vec3 indigo = u_deep;
  vec3 violet = u_body;
  vec3 blue = u_current;
  vec3 wisp = u_wisp;
  vec3 col = mix(voidc, indigo, smoothstep(0.22, 0.58, d));
  col = mix(col, violet, smoothstep(0.46, 0.8, d) * smoothstep(0.35, 0.85, length(q)));
  col = mix(col, blue, smoothstep(0.48, 0.85, w.x) * 0.62);
  col += wisp * pow(smoothstep(0.58, 0.9, d), 2.0) * 0.22;

  // Darker toward the edges, and a touch of noise so the gradients never band.
  float vig = smoothstep(1.25, 0.15, length((uv - 0.5) * vec2(1.15, 1.0)));
  col *= 0.55 + 0.45 * vig;
  col = mix(col, vec3(dot(col, vec3(0.299, 0.587, 0.114))), u_desat);
  if (u_eyes > 0.0) {
    // Their light falls on the smoke turning round them: ember on the thick of it, fading with
    // distance and breathing slowly. Under each eye a hollow, and an ember rim round it.
    float da = length(p0 - ea) / er;
    float db = length(p0 - eb) / er;
    float glow = exp(-da / 2.6) + exp(-db / 2.6);
    float hollow = max(exp(-da * da * 0.5), exp(-db * db * 0.5));
    float rim = max(exp(-pow(da - 1.7, 2.0) * 1.4), exp(-pow(db - 1.7, 2.0) * 1.4));
    float breath = 0.6 + 0.4 * sin(t * 0.5);
    col *= 1.0 - u_eyes * (0.5 * hollow + 0.1);
    col += u_eyes * vec3(0.66, 0.13, 0.05) * glow * breath * smoothstep(0.3, 0.78, d) * 0.85;
    col += u_eyes * vec3(0.2, 0.03, 0.02) * glow * 0.35;
    col += u_eyes * vec3(0.55, 0.07, 0.03) * rim * breath * smoothstep(0.3, 0.7, d);
  }
  // A touch of noise so the gradients never band.
  col += (hash(gl_FragCoord.xy + fract(t)) - 0.5) * (2.0 / 255.0);
  gl_FragColor = vec4(col, 1.0);
}
`;

/** Canvas pixels per CSS pixel: the smoke is soft, so a fraction of the resolution is enough.
    It is the one thing in the game not drawn in pixels: smooth, behind everything that is. */
const SCALE = 0.5;
const FRAME_MS = 1000 / 30;
/** A device that cannot keep up gets fewer pixels (src/ui/frameWatch.ts), down to this; below
    it the background holds still. */
const MIN_SCALE = 0.2;
/** The time of the still frame (reduced motion): chosen for a pleasant swirl. */
const STILL_TIME = 83;
/** A change of scene cross-fades over this long (ms). */
const FADE_MS = 2400;
/** The void as it always was, in the shader's own values (kept exact for the duel). */
const CLASSIC: Palette = {
  void: [0.018, 0.014, 0.04],
  deep: [0.105, 0.07, 0.26],
  body: [0.36, 0.16, 0.6],
  current: [0.09, 0.2, 0.55],
  wisp: [0.72, 0.68, 1.0],
  speed: 1,
  twist: 1,
};
const LAMP_WISP = rgb('#ffcf8a');

/** What the shader is given for a scene at a moment: its palette, with the scene's changes. */
interface Look {
  readonly colors: readonly Rgb[];
  readonly twist: number;
  readonly desat: number;
}

const mixRgb = (a: Rgb, b: Rgb, k: number): Rgb => [
  a[0] + (b[0] - a[0]) * k,
  a[1] + (b[1] - a[1]) * k,
  a[2] + (b[2] - a[2]) * k,
];

/** `moving`: a still smoke (reduced motion) keeps its wisp through a flare, and doesn't shimmer. */
function lookOf(scene: Scene, now: number, moving = true): Look {
  const base = scene.smoke === 'void' ? CLASSIC : SMOKE[scene.smoke];
  let body = base.body;
  let current = base.current;
  if (scene.iridescent && moving) {
    // The Magpie: body and current trade places, slowly, over 40 s.
    const k = 0.5 - 0.5 * Math.cos((now / 40000) * Math.PI * 2);
    body = mixRgb(base.body, base.current, k);
    current = mixRgb(base.current, base.body, k);
  }
  if (scene.tint) body = mixRgb(body, rgb(scene.tint), 0.75);
  const wisp = moving && flaring(now) ? LAMP_WISP : base.wisp;
  return {
    colors: [base.void, base.deep, body, current, wisp],
    twist: base.twist * scene.twist,
    desat: scene.desaturate,
  };
}

function mixLook(a: Look, b: Look, k: number): Look {
  return {
    colors: a.colors.map((c, i) => mixRgb(c, b.colors[i]!, k)),
    twist: a.twist + (b.twist - a.twist) * k,
    desat: a.desat + (b.desat - a.desat) * k,
  };
}

/** How fast the smoke turns in a scene, the void's own pace being 1 (negative: backward). */
function speedOf(scene: Scene): number {
  if (scene.held) return 0;
  const base = scene.smoke === 'void' ? 1 : SMOKE[scene.smoke].speed;
  return base * scene.speed * (scene.hush ? 0.2 : 1) * (scene.reverse ? -1 : 1);
}

export function SpaceBackground({ still: stillProp }: { still: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let still = stillProp;
    const canvas = ref.current;
    if (!canvas) return;
    let gl: WebGLRenderingContext | null;
    try {
      gl = canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        powerPreference: 'low-power',
      });
    } catch {
      gl = null;
    }
    if (!gl) return;
    if (softwareRendered(gl)) still = true;
    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = gl.createProgram();
    if (!vs || !fs || !program) return;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    // One triangle that covers the screen.
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
    const uRes = gl.getUniformLocation(program, 'u_res');
    const uTime = gl.getUniformLocation(program, 'u_time');
    const uColors = ['u_void', 'u_deep', 'u_body', 'u_current', 'u_wisp'].map((n) =>
      gl.getUniformLocation(program, n),
    );
    const uTwist = gl.getUniformLocation(program, 'u_twist');
    const uDesat = gl.getUniformLocation(program, 'u_desat');
    const uEyeA = gl.getUniformLocation(program, 'u_eyeA');
    const uEyeB = gl.getUniformLocation(program, 'u_eyeB');
    const uEyeR = gl.getUniformLocation(program, 'u_eyeR');
    const uEyes = gl.getUniformLocation(program, 'u_eyes');
    // His eyes, found on the page (BrokerEyes at Closing Time); the smoke eases toward them.
    let eyes = 0;
    let eyesAt = { ax: 0, ay: 0, bx: 0, by: 0, r: 1 };
    let eyesLooked = 0;
    const findEyes = (now: number) => {
      if (now - eyesLooked < 400) return;
      eyesLooked = now;
      const lids = document.querySelectorAll('.be:not(.is-small) > .be-lid');
      const a = lids[0]?.getBoundingClientRect();
      const b = lids[1]?.getBoundingClientRect();
      if (!a || !b || a.width === 0) {
        eyesAt = { ...eyesAt, r: 0 };
        return;
      }
      const k = canvas.width / Math.max(1, canvas.clientWidth);
      const h = canvas.height;
      eyesAt = {
        ax: (a.left + a.width / 2) * k,
        ay: h - (a.top + a.height / 2) * k,
        bx: (b.left + b.width / 2) * k,
        by: h - (b.top + b.height / 2) * k,
        r: (a.width / 2) * k,
      };
    };

    const context = gl;
    let frame = 0;
    let last = 0;
    let scale = SCALE;
    const watch = new FrameWatch();
    // The smoke's own clock: it runs at the scene's speed, and backward when the scene says.
    let clock = STILL_TIME;
    let clockAt = performance.now();
    // The cross-fade between scenes: from the look on show when the scene changed, to the new one.
    let from: Look = lookOf(getScene(), clockAt);
    let fadeStart = -Infinity;
    let scene = getScene();
    const lookNow = (now: number): Look => {
      const to = lookOf(scene, now);
      const k = Math.min(1, (now - fadeStart) / FADE_MS);
      return k >= 1 ? to : mixLook(from, to, k * k * (3 - 2 * k));
    };
    const draw = (time: number, now = performance.now()) => {
      const look = still ? lookOf(scene, now, false) : lookNow(now);
      look.colors.forEach((c, i) => context.uniform3f(uColors[i]!, c[0], c[1], c[2]));
      context.uniform1f(uTwist, look.twist);
      context.uniform1f(uDesat, look.desat);
      findEyes(now);
      eyes += ((eyesAt.r > 0 ? 1 : 0) - eyes) * (still ? 1 : 0.04);
      context.uniform2f(uEyeA, eyesAt.ax, eyesAt.ay);
      context.uniform2f(uEyeB, eyesAt.bx, eyesAt.by);
      context.uniform1f(uEyeR, Math.max(1, eyesAt.r));
      context.uniform1f(uEyes, eyes);
      context.uniform1f(uTime, time);
      context.drawArrays(context.TRIANGLES, 0, 3);
      canvas.classList.add('is-ready');
    };
    const unsubscribe = subscribeScene(() => {
      const now = performance.now();
      const next = getScene();
      if (next === scene) {
        // A flare: nothing to fade, the next frame shows it.
        if (still) draw(STILL_TIME, now);
        return;
      }
      from = lookNow(now);
      fadeStart = now;
      scene = next;
      // A still smoke changes palette at once, in one new still frame.
      if (still) draw(STILL_TIME, now);
    });
    const resize = () => {
      const w = Math.max(1, Math.round(canvas.clientWidth * scale));
      const h = Math.max(1, Math.round(canvas.clientHeight * scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        context.viewport(0, 0, w, h);
      }
      context.uniform2f(uRes, w, h);
      if (still) draw(STILL_TIME);
    };
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      // Frames keep coming late: fewer pixels, and in the end a still picture (the last frame
      // drawn stays on the canvas).
      if (watch.struggling(now)) {
        scale *= 0.7;
        if (scale < MIN_SCALE) {
          still = true;
          cancelAnimationFrame(frame);
          return;
        }
        // A resized canvas is blank: draw it again straight away.
        resize();
        last = 0;
      }
      if (now - last < FRAME_MS) return;
      last = now;
      clock += ((now - clockAt) / 1000) * speedOf(scene);
      clockAt = now;
      draw(clock, now);
    };
    const onVisibility = () => {
      cancelAnimationFrame(frame);
      watch.reset();
      clockAt = performance.now();
      if (!document.hidden && !still) frame = requestAnimationFrame(tick);
    };
    resize();
    window.addEventListener('resize', resize);
    if (!still) {
      document.addEventListener('visibilitychange', onVisibility);
      frame = requestAnimationFrame(tick);
    }
    const onLost = (e: Event) => {
      e.preventDefault();
      cancelAnimationFrame(frame);
    };
    canvas.addEventListener('webglcontextlost', onLost);
    return () => {
      cancelAnimationFrame(frame);
      unsubscribe();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('webglcontextlost', onLost);
      context.deleteBuffer(buffer);
      context.deleteProgram(program);
      context.deleteShader(vs);
      context.deleteShader(fs);
    };
  }, [stillProp]);
  return (
    <>
      <canvas ref={ref} className="space-canvas" aria-hidden="true" />
      <StarField still={stillProp} />
    </>
  );
}
